// src/components/admin/WalletTopupsTab.jsx
// Global, cross-customer list of wallet top-up requests for a salon, so admin
// doesn't have to open each Customer Details Page individually to find
// pending requests. Mirrors BookingRequestsTab's pending-count-badge /
// sub-tab convention. Also lets admin configure up to 3 top-up packages
// (pay amount + wallet credit, which may differ as a promotional bonus).
// Also includes Service Packages: service-based session bundles with paid
// and complimentary session counts.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, updateDoc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { verifyTopup, rejectTopup } from '../../utils/walletTopupActions';
import { getSalonServices, getSalonStaff } from '../../api/salonApi';
import '../../styles/booking-requests.css';

const DEFAULT_PACKAGES = [
  { payAmount: '100', creditAmount: '100' },
  { payAmount: '500', creditAmount: '500' },
  { payAmount: '1000', creditAmount: '1000' }
];

const MAX_PACKAGES = 3;

function formatCurrency(amount) {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
}

function formatDate(date) {
  if (!date) return 'N/A';
  const d = date?.toDate ? date.toDate() : date?.seconds ? new Date(date.seconds * 1000) : new Date(date);
  return d.toLocaleString();
}

function TopupPackagesEditor({ salonId }) {
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  useEffect(() => {
    if (!salonId) return;
    (async () => {
      try {
        const salonSnap = await getDoc(doc(db, 'salons', salonId));
        const configured = salonSnap.exists() ? salonSnap.data().walletTopupPackages : null;
        if (Array.isArray(configured) && configured.length > 0) {
          setPackages(configured.map(p => ({
            payAmount: String(p.payAmount ?? ''),
            creditAmount: String(p.creditAmount ?? '')
          })));
        }
      } catch (err) {
        console.error('Error loading top-up packages:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [salonId]);

  const updateRow = (index, field, value) => {
    setPackages(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const addRow = () => {
    if (packages.length >= MAX_PACKAGES) return;
    setPackages(prev => [...prev, { payAmount: '', creditAmount: '' }]);
  };

  const removeRow = (index) => {
    setPackages(prev => prev.filter((_, i) => i !== index));
  };

  const isValid = packages.length > 0 && packages.every(p =>
    parseFloat(p.payAmount) > 0 && parseFloat(p.creditAmount) > 0
  );

  const handleSave = async () => {
    if (!isValid) {
      setMessage('Each cash package needs a positive pay amount and credit amount.');
      setMessageIsError(true);
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const cleanPackages = packages.map(p => ({
        payAmount: parseFloat(p.payAmount),
        creditAmount: parseFloat(p.creditAmount)
      }));
      await updateDoc(doc(db, 'salons', salonId), { walletTopupPackages: cleanPackages });
      setMessage('Cash packages saved successfully.');
      setMessageIsError(false);
    } catch (err) {
      console.error('Error saving cash packages:', err);
      setMessage('Failed to save cash packages.');
      setMessageIsError(true);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  if (loading) {
    return <p>Loading cash packages...</p>;
  }

  return (
    <div>
      <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Set up to {MAX_PACKAGES} cash packages. The customer pays the amount on the left; the amount on the
        right is credited to their wallet — set it higher to offer a cash top-up bonus.
      </p>

      {message && (
        <div style={{ marginBottom: '12px', color: messageIsError ? '#c0392b' : '#1a7a3c', fontSize: '13px' }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '520px' }}>
        {packages.map((pkg, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Customer Pays
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pkg.payAmount}
                onChange={(e) => updateRow(index, 'payAmount', e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Wallet Credit
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pkg.creditAmount}
                onChange={(e) => updateRow(index, 'creditAmount', e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <button
              className="delete-record-button"
              style={{ marginTop: '18px' }}
              disabled={packages.length <= 1}
              onClick={() => removeRow(index)}
              title="Remove package"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button
          className="edit-transaction-button"
          onClick={addRow}
          disabled={packages.length >= MAX_PACKAGES}
        >
          + Add Package
        </button>
        <button
          className="add-transaction-button"
          onClick={handleSave}
          disabled={saving || !isValid}
        >
          {saving ? 'Saving...' : 'Save Cash Packages'}
        </button>
      </div>
    </div>
  );
}

// ── Service Packages ──────────────────────────────────────────────────────────

const DEFAULT_SERVICE_PACKAGES = [];
const MAX_SERVICE_PACKAGES = 10;

function buildEmptyServicePackage() {
  return {
    name: '',
    price: '',
    durationMinutes: '',
    services: [],   // { serviceId, serviceName, sessionsPerVisit }
    staffIds: [],
    paidSessions: '',
    complimentarySessions: '',
    // totalSessions is derived: paidSessions + complimentarySessions
  };
}

function ServicePackagesEditor({ salonId }) {
  const [services, setServices] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [packages, setPackages] = useState(DEFAULT_SERVICE_PACKAGES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  useEffect(() => {
    if (!salonId) return;
    let cancelled = false;
    (async () => {
      try {
        const [salonSnap, svcList, staffList] = await Promise.all([
          getDoc(doc(db, 'salons', salonId)),
          getSalonServices(salonId),
          getSalonStaff(salonId),
        ]);
        if (cancelled) return;
        setServices(svcList.filter(s => !s.deleted));
        setAllStaff(staffList.filter(s => s.isActive !== false));
        const stored = salonSnap.exists() ? salonSnap.data().servicePackages : null;
        if (Array.isArray(stored) && stored.length > 0) {
          setPackages(stored.map(p => ({
            name: p.name ?? '',
            price: String(p.price ?? ''),
            durationMinutes: String(p.durationMinutes ?? ''),
            services: Array.isArray(p.services) ? p.services : [],
            staffIds: Array.isArray(p.staffIds) ? p.staffIds : [],
            paidSessions: String(p.paidSessions ?? ''),
            complimentarySessions: String(p.complimentarySessions ?? ''),
          })));
        }
      } catch (err) {
        console.error('ServicePackagesEditor load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [salonId]);

  const updatePackageField = (pkgIdx, field, value) => {
    setPackages(prev => prev.map((p, i) => i !== pkgIdx ? p : { ...p, [field]: value }));
  };

  const addServiceRow = (pkgIdx) => {
    setPackages(prev => prev.map((p, i) => {
      if (i !== pkgIdx) return p;
      return { ...p, services: [...p.services, { serviceId: '', serviceName: '', sessionsPerVisit: '' }] };
    }));
  };

  const updateServiceRow = (pkgIdx, svcIdx, field, value) => {
    setPackages(prev => prev.map((p, i) => {
      if (i !== pkgIdx) return p;
      const updated = p.services.map((s, j) => {
        if (j !== svcIdx) return s;
        if (field === 'serviceId') {
          const found = services.find(sv => sv.id === value);
          return { ...s, serviceId: value, serviceName: found ? found.name : '' };
        }
        return { ...s, [field]: value };
      });
      return { ...p, services: updated };
    }));
  };

  const removeServiceRow = (pkgIdx, svcIdx) => {
    setPackages(prev => prev.map((p, i) => {
      if (i !== pkgIdx) return p;
      return { ...p, services: p.services.filter((_, j) => j !== svcIdx) };
    }));
  };

  const addPackage = () => {
    if (packages.length >= MAX_SERVICE_PACKAGES) return;
    setPackages(prev => [...prev, buildEmptyServicePackage()]);
  };

  const removePackage = (pkgIdx) => {
    setPackages(prev => prev.filter((_, i) => i !== pkgIdx));
  };

  const isValid = packages.every(p => {
    const paid = parseInt(p.paidSessions) || 0;
    const comp = parseInt(p.complimentarySessions) || 0;
    return (
      p.name.trim() &&
      parseFloat(p.price) > 0 &&
      parseInt(p.durationMinutes) > 0 &&
      p.services.length > 0 &&
      p.services.every(s => s.serviceId && parseInt(s.sessionsPerVisit) > 0) &&
      (paid + comp) > 0
    );
  });

  const handleSave = async () => {
    if (!isValid && packages.length > 0) {
      setMessage('Each package needs a name, price, duration, at least one service, and at least 1 total session (paid + complimentary).');
      setMessageIsError(true);
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const clean = packages.map(p => {
        const paid = parseInt(p.paidSessions) || 0;
        const comp = parseInt(p.complimentarySessions) || 0;
        return {
          name: p.name.trim(),
          price: parseFloat(p.price),
          durationMinutes: parseInt(p.durationMinutes),
          services: p.services.map(s => ({
            serviceId: s.serviceId,
            serviceName: s.serviceName,
            sessionsPerVisit: parseInt(s.sessionsPerVisit),
          })),
          staffIds: p.staffIds,
          paidSessions: paid,
          complimentarySessions: comp,
          totalSessions: paid + comp,
        };
      });
      await updateDoc(doc(db, 'salons', salonId), { servicePackages: clean });
      setMessage('Service packages saved successfully.');
      setMessageIsError(false);
    } catch (err) {
      console.error('ServicePackagesEditor save error:', err);
      setMessage('Failed to save service packages.');
      setMessageIsError(true);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  if (loading) return <p>Loading service packages...</p>;

  return (
    <div>
      <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Define service-based packages. Choose which services count toward a session, set how many
        sessions form a package, then split sessions into Paid and Complimentary (bonus) sessions.
        Paid + Complimentary must equal Total Sessions.
      </p>

      {message && (
        <div style={{ marginBottom: '12px', color: messageIsError ? '#c0392b' : '#1a7a3c', fontSize: '13px' }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {packages.map((pkg, pkgIdx) => (
          <div key={pkgIdx} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px' }}>Package {pkgIdx + 1}</strong>
              <button className="delete-record-button" onClick={() => removePackage(pkgIdx)}>Remove Package</button>
            </div>

            {/* Package name */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Package Name</label>
              <input
                type="text"
                value={pkg.name}
                onChange={e => updatePackageField(pkgIdx, 'name', e.target.value)}
                placeholder="e.g. Hair Treatment Bundle"
                style={{ width: '100%', maxWidth: '360px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>

            {/* Price & Duration */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Package Price ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pkg.price}
                  onChange={e => updatePackageField(pkgIdx, 'price', e.target.value)}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Duration for each session (minutes)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={pkg.durationMinutes}
                  onChange={e => updatePackageField(pkgIdx, 'durationMinutes', e.target.value)}
                  placeholder="e.g. 60"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
            </div>

            {/* Staff selection */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                Assigned Staff (select all that apply)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allStaff.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pkg.staffIds.includes(s.id)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...pkg.staffIds, s.id]
                          : pkg.staffIds.filter(id => id !== s.id);
                        updatePackageField(pkgIdx, 'staffIds', next);
                      }}
                    />
                    {s.name}
                  </label>
                ))}
                {allStaff.length === 0 && (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>No staff found for this salon.</span>
                )}
              </div>
            </div>

            {/* Services in this package */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Services &amp; Sessions per Visit</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pkg.services.map((svc, svcIdx) => (
                  <div key={svcIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <select
                      value={svc.serviceId}
                      onChange={e => updateServiceRow(pkgIdx, svcIdx, 'serviceId', e.target.value)}
                      style={{ flex: 2, padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    >
                      <option value="">— Select service —</option>
                      {services.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <div style={{ flex: 1 }}>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={svc.sessionsPerVisit}
                        onChange={e => updateServiceRow(pkgIdx, svcIdx, 'sessionsPerVisit', e.target.value)}
                        placeholder="Sessions / visit"
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                      />
                    </div>
                    <button
                      className="delete-record-button"
                      onClick={() => removeServiceRow(pkgIdx, svcIdx)}
                      disabled={pkg.services.length <= 1}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="edit-transaction-button"
                style={{ marginTop: '8px' }}
                onClick={() => addServiceRow(pkgIdx)}
              >
                + Add Service
              </button>
            </div>

            {/* Session counts — Total auto-derives from Paid + Complimentary */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Paid Sessions</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={pkg.paidSessions}
                  onChange={e => updatePackageField(pkgIdx, 'paidSessions', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Complimentary Sessions</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={pkg.complimentarySessions}
                  onChange={e => updatePackageField(pkgIdx, 'complimentarySessions', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Sessions</label>
                <div style={{
                  padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                  background: '#f9fafb', fontSize: '14px', fontWeight: 600, color: '#374151'
                }}>
                  {(parseInt(pkg.paidSessions) || 0) + (parseInt(pkg.complimentarySessions) || 0)}
                </div>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>auto-calculated</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button
          className="edit-transaction-button"
          onClick={addPackage}
          disabled={packages.length >= MAX_SERVICE_PACKAGES}
        >
          + Add Package
        </button>
        <button
          className="add-transaction-button"
          onClick={handleSave}
          disabled={saving || (packages.length > 0 && !isValid)}
        >
          {saving ? 'Saving...' : 'Save Service Packages'}
        </button>
      </div>
    </div>
  );
}

// ── Main tab component ────────────────────────────────────────────────────────

function WalletTopupsTab({ salonId }) {
  const navigate = useNavigate();
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('pending');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!salonId) return;
    setLoading(true);

    const unsub = onSnapshot(
      query(
        collection(db, 'transactions'),
        where('salonId', '==', salonId),
        where('type', '==', 'wallet_topup')
      ),
      snap => {
        // A pending request with no proof uploaded yet is an abandoned draft,
        // not a real top-up request — never show it to admin.
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(t => t.proofImageUrl || ['verified', 'rejected'].includes(t.walletTopupStatus))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setTopups(data);
        setLoading(false);
      },
      err => {
        console.error('WalletTopupsTab: listener error', err);
        setLoading(false);
      }
    );

    return unsub;
  }, [salonId]);

  const pendingTopups  = topups.filter(t => t.walletTopupStatus === 'pending');
  const verifiedTopups = topups.filter(t => t.walletTopupStatus === 'verified');
  const rejectedTopups = topups.filter(t => t.walletTopupStatus === 'rejected');

  const visibleTopups =
    activeSubTab === 'pending'  ? pendingTopups :
    activeSubTab === 'verified' ? verifiedTopups :
    rejectedTopups;

  const handleVerify = async (topup) => {
    setError('');
    setActionLoadingId(topup.id);
    try {
      await verifyTopup(topup.id, salonId);
    } catch (err) {
      console.error('Error verifying top-up:', err);
      setError(err.message || 'Failed to verify top-up');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (topup) => {
    setError('');
    setActionLoadingId(topup.id);
    try {
      await rejectTopup(topup.id, salonId);
    } catch (err) {
      console.error('Error rejecting top-up:', err);
      setError(err.message || 'Failed to reject top-up');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="tab-content">

      <h2 style={{
        margin: '0 0 28px',
        fontSize: '28px',
        fontWeight: 600,
        fontFamily: "'Familjen Grotesk', sans-serif",
        color: 'var(--primary-coral)',
        borderBottom: '2px solid var(--light-coral)',
        paddingBottom: '10px',
      }}>Packages</h2>

      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ marginBottom: '4px' }}>Service Packages</h3>
        <ServicePackagesEditor salonId={salonId} />
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h3 style={{ marginBottom: '4px' }}>Cash Packages</h3>
        <TopupPackagesEditor salonId={salonId} />
      </div>

      <div>
        <h3>Cash Top-up History</h3>

        {error && <div className="empty-state"><p>{error}</p></div>}

        <div className="requests-tabs-header">
          <div className="requests-tabs">
            <button
              className={`requests-tab ${activeSubTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('pending')}
            >
              Pending Review
              {pendingTopups.length > 0 && (
                <span className="requests-count-badge">{pendingTopups.length}</span>
              )}
            </button>
            <button
              className={`requests-tab ${activeSubTab === 'verified' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('verified')}
            >
              Verified
            </button>
            <button
              className={`requests-tab ${activeSubTab === 'rejected' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('rejected')}
            >
              Rejected
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading wallet top-up requests...</p>
        ) : visibleTopups.length === 0 ? (
          <div className="empty-state">
            <p>No {activeSubTab} wallet top-up requests.</p>
          </div>
        ) : (
          <div className="data-table-container" style={{ display: 'block', width: '100%' }}>
            <table className="data-table" style={{ display: 'table', width: '100%' }}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Pay Amount</th>
                  <th>Wallet Credit</th>
                  <th>Requested At</th>
                  <th>Proof</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTopups.map(topup => (
                  <tr key={topup.id}>
                    <td>
                      <button
                        className="link-button"
                        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }}
                        onClick={() => navigate(`/admin/customers/${salonId}/${topup.customerId}`)}
                      >
                        {topup.customerName || topup.customerEmail}
                      </button>
                    </td>
                    <td>{formatCurrency(topup.amount)}</td>
                    <td>{formatCurrency(topup.creditAmount ?? topup.amount)}</td>
                    <td>{formatDate(topup.createdAt)}</td>
                    <td>
                      {topup.proofImageUrl ? (
                        <a href={topup.proofImageUrl} target="_blank" rel="noopener noreferrer">View Proof</a>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>Awaiting upload</span>
                      )}
                    </td>
                    <td>
                      {topup.walletTopupStatus === 'pending' ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="edit-transaction-button"
                            disabled={actionLoadingId === topup.id}
                            onClick={() => handleVerify(topup)}
                          >
                            {actionLoadingId === topup.id ? 'Working...' : 'Verify'}
                          </button>
                          <button
                            className="delete-record-button"
                            disabled={actionLoadingId === topup.id}
                            onClick={() => handleReject(topup)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span style={{ textTransform: 'capitalize' }}>{topup.walletTopupStatus}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WalletTopupsTab;
